import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';

export interface HourlySalesData {
  hour: string;
  sales: number;
  orderCount: number;
  itemsSold: number;
  lastWeekSales: number;
  lastWeekOrderCount: number;
  lastWeekItemsSold: number;
}

export interface AnalyticsSummary {
  // Sales
  totalSales: number;
  lastWeekTotalSales: number;
  salesDifference: number;
  salesPercentChange: number;
  
  // Orders
  orderCount: number;
  lastWeekOrderCount: number;
  orderDifference: number;
  orderPercentChange: number;
  
  // Average Order Value (Avg Ticket)
  averageOrderValue: number;
  lastWeekAverageOrderValue: number;
  aovDifference: number;
  aovPercentChange: number;
  
  // Items per sale (legacy)
  averageItemsPerSale: number;
  lastWeekAverageItemsPerSale: number;
  itemsDifference: number;
  
  // Guest Count
  guestCount: number;
  lastWeekGuestCount: number;
  guestCountDifference: number;
  guestCountPercentChange: number;
  
  // Items Sold
  itemsSold: number;
  lastWeekItemsSold: number;
  itemsSoldDifference: number;
  itemsSoldPercentChange: number;
  
  // Avg Prep Time (in minutes)
  avgPrepTime: number;
  lastWeekAvgPrepTime: number;
  avgPrepTimeDifference: number;
  avgPrepTimePercentChange: number;
  
  comparisonDayName: string;
}

export type StatKey = 
  | 'todays_sales' 
  | 'order_count' 
  | 'avg_ticket' 
  | 'guest_count' 
  | 'avg_prep_time' 
  | 'items_sold';

export const AVAILABLE_STATS: { key: StatKey; label: string; description: string }[] = [
  { key: 'todays_sales', label: "Today's Sales", description: 'Total revenue for the day' },
  { key: 'order_count', label: 'Orders', description: 'Total number of orders' },
  { key: 'avg_ticket', label: 'Avg. Ticket', description: 'Average order value' },
  { key: 'guest_count', label: 'Guests Served', description: 'Total guests served today' },
  { key: 'avg_prep_time', label: 'Avg. Prep Time', description: 'Average order preparation time' },
  { key: 'items_sold', label: 'Items Sold', description: 'Total quantity of items sold' },
];

export const DEFAULT_STATS: StatKey[] = ['todays_sales', 'order_count', 'avg_ticket', 'guest_count'];

function getStartOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getEndOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function getStartOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  // Start of week (Sunday = 0)
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getStartOfMonth(date: Date): Date {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getDayName(date: Date): string {
  return date.toLocaleDateString('en-US', { weekday: 'long' });
}

interface DateRange {
  start: Date;
  end: Date;
  comparisonStart: Date;
  comparisonEnd: Date;
  comparisonLabel: string;
  periodLabel: string;
}

function getDateRangeForPeriod(timePeriod: TimePeriod): DateRange {
  const now = new Date();

  switch (timePeriod) {
    case 'today': {
      const start = getStartOfDay(now);
      const end = getEndOfDay(now);
      // Compare to same day last week
      const comparisonStart = new Date(start);
      comparisonStart.setDate(comparisonStart.getDate() - 7);
      const comparisonEnd = new Date(end);
      comparisonEnd.setDate(comparisonEnd.getDate() - 7);
      return {
        start,
        end,
        comparisonStart,
        comparisonEnd,
        comparisonLabel: getDayName(comparisonStart),
        periodLabel: 'today',
      };
    }
    case 'week': {
      const start = getStartOfWeek(now);
      const end = getEndOfDay(now);
      // Compare to same week last week (previous week)
      const comparisonStart = new Date(start);
      comparisonStart.setDate(comparisonStart.getDate() - 7);
      const comparisonEnd = new Date(end);
      comparisonEnd.setDate(comparisonEnd.getDate() - 7);
      return {
        start,
        end,
        comparisonStart,
        comparisonEnd,
        comparisonLabel: 'week',
        periodLabel: 'this week',
      };
    }
    case 'month': {
      const start = getStartOfMonth(now);
      const end = getEndOfDay(now);
      // Compare to same period last month
      const comparisonStart = new Date(start);
      comparisonStart.setMonth(comparisonStart.getMonth() - 1);
      const comparisonEnd = new Date(end);
      comparisonEnd.setMonth(comparisonEnd.getMonth() - 1);
      return {
        start,
        end,
        comparisonStart,
        comparisonEnd,
        comparisonLabel: 'month',
        periodLabel: 'this month',
      };
    }
  }
}

function calculatePercentChange(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
}

export type TimePeriod = 'today' | 'week' | 'month';

interface UseAnalyticsOptions {
  globalMode?: boolean;
  locationIds?: string[];
  timePeriod?: TimePeriod;
}

export function useAnalytics(options: UseAnalyticsOptions = {}) {
  const queryClient = useQueryClient();
  const { currentLocation, locations } = useLocationStore();
  const { globalMode = false, locationIds, timePeriod = 'today' } = options;

  // Determine which location IDs to query
  const effectiveLocationIds = globalMode
    ? (locationIds ?? locations.map(l => l.id))
    : currentLocation?.id
      ? [currentLocation.id]
      : [];

  const queryKey = globalMode
    ? ['analytics', 'global', effectiveLocationIds.sort().join(','), timePeriod]
    : ['analytics', currentLocation?.id, timePeriod];

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      if (effectiveLocationIds.length === 0) {
        return {
          hourlyData: [] as HourlySalesData[],
          summary: null as AnalyticsSummary | null,
        };
      }

      // Get date ranges based on selected time period
      const dateRange = getDateRangeForPeriod(timePeriod);
      const { start: periodStart, end: periodEnd, comparisonStart, comparisonEnd, comparisonLabel } = dateRange;

      // Fetch current period orders with items and guest count
      let periodOrdersQuery = supabase
        .from('orders')
        .select(`
          id,
          total,
          guest_count,
          created_at,
          order_items (
            id,
            quantity
          )
        `)
        .gte('created_at', periodStart.toISOString())
        .lte('created_at', periodEnd.toISOString())
        .in('status', ['completed', 'ready', 'in_progress', 'sent']);

      if (effectiveLocationIds.length === 1) {
        periodOrdersQuery = periodOrdersQuery.eq('location_id', effectiveLocationIds[0]);
      } else {
        periodOrdersQuery = periodOrdersQuery.in('location_id', effectiveLocationIds);
      }

      const { data: periodOrders, error: periodError } = await periodOrdersQuery;
      if (periodError) throw periodError;

      // Fetch comparison period orders with items and guest count
      let comparisonOrdersQuery = supabase
        .from('orders')
        .select(`
          id,
          total,
          guest_count,
          created_at,
          order_items (
            id,
            quantity
          )
        `)
        .gte('created_at', comparisonStart.toISOString())
        .lte('created_at', comparisonEnd.toISOString())
        .in('status', ['completed', 'ready', 'in_progress', 'sent']);

      if (effectiveLocationIds.length === 1) {
        comparisonOrdersQuery = comparisonOrdersQuery.eq('location_id', effectiveLocationIds[0]);
      } else {
        comparisonOrdersQuery = comparisonOrdersQuery.in('location_id', effectiveLocationIds);
      }

      const { data: comparisonOrders, error: comparisonError } = await comparisonOrdersQuery;
      if (comparisonError) throw comparisonError;

      // Fetch kitchen orders for prep time calculation (current period)
      const periodOrderIds = (periodOrders || []).map(o => o.id);
      let periodKitchenOrders: any[] = [];
      if (periodOrderIds.length > 0) {
        const { data: kitchenData } = await supabase
          .from('kitchen_orders')
          .select('order_id, sent_at, completed_at, status')
          .in('order_id', periodOrderIds)
          .eq('status', 'completed');
        periodKitchenOrders = kitchenData || [];
      }

      // Fetch kitchen orders for prep time calculation (comparison period)
      const comparisonOrderIds = (comparisonOrders || []).map(o => o.id);
      let comparisonKitchenOrders: any[] = [];
      if (comparisonOrderIds.length > 0) {
        const { data: kitchenData } = await supabase
          .from('kitchen_orders')
          .select('order_id, sent_at, completed_at, status')
          .in('order_id', comparisonOrderIds)
          .eq('status', 'completed');
        comparisonKitchenOrders = kitchenData || [];
      }

      // Group data for chart - hourly for 'today', daily for 'week'/'month'
      const periodMap: Record<string, { sales: number; orderCount: number; itemsSold: number }> = {};
      const comparisonMap: Record<string, { sales: number; orderCount: number; itemsSold: number }> = {};

      // Initialize time buckets based on period
      if (timePeriod === 'today') {
        // Hourly buckets for today
        for (let h = 0; h < 24; h++) {
          const hourStr = formatHour(h);
          periodMap[hourStr] = { sales: 0, orderCount: 0, itemsSold: 0 };
          comparisonMap[hourStr] = { sales: 0, orderCount: 0, itemsSold: 0 };
        }
      } else {
        // Daily buckets for week/month
        const dayCount = timePeriod === 'week' ? 7 : 31;
        for (let d = 0; d < dayCount; d++) {
          const date = new Date(periodStart);
          date.setDate(date.getDate() + d);
          if (date > periodEnd) break;
          const dayStr = formatDay(date);
          periodMap[dayStr] = { sales: 0, orderCount: 0, itemsSold: 0 };
          comparisonMap[dayStr] = { sales: 0, orderCount: 0, itemsSold: 0 };
        }
      }

      // Process current period orders
      let periodTotalItems = 0;
      let periodGuestCount = 0;
      (periodOrders || []).forEach(order => {
        const orderDate = new Date(order.created_at);
        const bucketKey = timePeriod === 'today'
          ? formatHour(orderDate.getHours())
          : formatDay(orderDate);

        if (periodMap[bucketKey]) {
          periodMap[bucketKey].sales += Number(order.total) || 0;
          periodMap[bucketKey].orderCount += 1;
        }

        // Count guests
        periodGuestCount += order.guest_count || 1;

        // Count items
        let orderItems = 0;
        (order.order_items || []).forEach((item: any) => {
          const qty = item.quantity || 1;
          periodTotalItems += qty;
          orderItems += qty;
        });
        if (periodMap[bucketKey]) {
          periodMap[bucketKey].itemsSold += orderItems;
        }
      });

      // Process comparison period orders
      let comparisonTotalItems = 0;
      let comparisonGuestCount = 0;
      (comparisonOrders || []).forEach(order => {
        const orderDate = new Date(order.created_at);
        // For comparison, we need to map to the same bucket position
        let bucketKey: string;
        if (timePeriod === 'today') {
          bucketKey = formatHour(orderDate.getHours());
        } else {
          // Map comparison date to equivalent day in current period
          const dayOffset = Math.floor((orderDate.getTime() - comparisonStart.getTime()) / (1000 * 60 * 60 * 24));
          const equivalentDate = new Date(periodStart);
          equivalentDate.setDate(equivalentDate.getDate() + dayOffset);
          bucketKey = formatDay(equivalentDate);
        }

        if (comparisonMap[bucketKey]) {
          comparisonMap[bucketKey].sales += Number(order.total) || 0;
          comparisonMap[bucketKey].orderCount += 1;
        }

        // Count guests
        comparisonGuestCount += order.guest_count || 1;

        // Count items
        let orderItems = 0;
        (order.order_items || []).forEach((item: any) => {
          const qty = item.quantity || 1;
          comparisonTotalItems += qty;
          orderItems += qty;
        });
        if (comparisonMap[bucketKey]) {
          comparisonMap[bucketKey].itemsSold += orderItems;
        }
      });

      // Calculate prep times
      const calculateAvgPrepTime = (kitchenOrders: any[]): number => {
        const validOrders = kitchenOrders.filter(ko => ko.sent_at && ko.completed_at);
        if (validOrders.length === 0) return 0;

        const totalMinutes = validOrders.reduce((sum, ko) => {
          const sentAt = new Date(ko.sent_at).getTime();
          const completedAt = new Date(ko.completed_at).getTime();
          const diffMinutes = (completedAt - sentAt) / (1000 * 60);
          return sum + diffMinutes;
        }, 0);

        return totalMinutes / validOrders.length;
      };

      const periodAvgPrepTime = calculateAvgPrepTime(periodKitchenOrders);
      const comparisonAvgPrepTime = calculateAvgPrepTime(comparisonKitchenOrders);

      // Build chart data array
      const hourlyData: HourlySalesData[] = [];
      const bucketKeys = Object.keys(periodMap);
      bucketKeys.forEach(key => {
        hourlyData.push({
          hour: key,
          sales: periodMap[key].sales,
          orderCount: periodMap[key].orderCount,
          itemsSold: periodMap[key].itemsSold,
          lastWeekSales: comparisonMap[key]?.sales || 0,
          lastWeekOrderCount: comparisonMap[key]?.orderCount || 0,
          lastWeekItemsSold: comparisonMap[key]?.itemsSold || 0,
        });
      });

      // Calculate summary
      const periodTotal = (periodOrders || []).reduce((sum, o) => sum + (Number(o.total) || 0), 0);
      const comparisonTotal = (comparisonOrders || []).reduce((sum, o) => sum + (Number(o.total) || 0), 0);

      const periodCount = periodOrders?.length || 0;
      const comparisonCount = comparisonOrders?.length || 0;

      const periodAov = periodCount > 0 ? periodTotal / periodCount : 0;
      const comparisonAov = comparisonCount > 0 ? comparisonTotal / comparisonCount : 0;

      const periodItemsPerSale = periodCount > 0 ? periodTotalItems / periodCount : 0;
      const comparisonItemsPerSale = comparisonCount > 0 ? comparisonTotalItems / comparisonCount : 0;

      const summary: AnalyticsSummary = {
        // Sales
        totalSales: periodTotal,
        lastWeekTotalSales: comparisonTotal,
        salesDifference: periodTotal - comparisonTotal,
        salesPercentChange: calculatePercentChange(periodTotal, comparisonTotal),

        // Orders
        orderCount: periodCount,
        lastWeekOrderCount: comparisonCount,
        orderDifference: periodCount - comparisonCount,
        orderPercentChange: calculatePercentChange(periodCount, comparisonCount),

        // AOV
        averageOrderValue: periodAov,
        lastWeekAverageOrderValue: comparisonAov,
        aovDifference: periodAov - comparisonAov,
        aovPercentChange: calculatePercentChange(periodAov, comparisonAov),

        // Items per sale (legacy)
        averageItemsPerSale: periodItemsPerSale,
        lastWeekAverageItemsPerSale: comparisonItemsPerSale,
        itemsDifference: periodItemsPerSale - comparisonItemsPerSale,

        // Guest Count
        guestCount: periodGuestCount,
        lastWeekGuestCount: comparisonGuestCount,
        guestCountDifference: periodGuestCount - comparisonGuestCount,
        guestCountPercentChange: calculatePercentChange(periodGuestCount, comparisonGuestCount),

        // Items Sold
        itemsSold: periodTotalItems,
        lastWeekItemsSold: comparisonTotalItems,
        itemsSoldDifference: periodTotalItems - comparisonTotalItems,
        itemsSoldPercentChange: calculatePercentChange(periodTotalItems, comparisonTotalItems),

        // Avg Prep Time
        avgPrepTime: periodAvgPrepTime,
        lastWeekAvgPrepTime: comparisonAvgPrepTime,
        avgPrepTimeDifference: periodAvgPrepTime - comparisonAvgPrepTime,
        avgPrepTimePercentChange: calculatePercentChange(periodAvgPrepTime, comparisonAvgPrepTime),

        comparisonDayName: comparisonLabel,
      };

      return { hourlyData, summary };
    },
    enabled: effectiveLocationIds.length > 0,
    refetchInterval: 60000, // Also refresh every minute as a fallback
  });

  // Subscribe to real-time updates for orders and kitchen_orders
  useEffect(() => {
    if (effectiveLocationIds.length === 0) return;

    const channels: ReturnType<typeof supabase.channel>[] = [];

    // Subscribe to orders changes for each location
    effectiveLocationIds.forEach((locationId) => {
      const ordersChannel = supabase
        .channel(`analytics-orders:${locationId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'orders',
            filter: `location_id=eq.${locationId}`,
          },
          () => {
            // Invalidate analytics query when orders change
            queryClient.invalidateQueries({ queryKey });
          }
        )
        .subscribe();

      channels.push(ordersChannel);
    });

    // Subscribe to kitchen_orders changes (for prep time updates)
    // Kitchen orders don't have location_id, so we listen to all and let the query filter
    const kitchenChannel = supabase
      .channel('analytics-kitchen')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'kitchen_orders',
        },
        (payload) => {
          // Only refresh if the kitchen order was completed
          if (payload.new && (payload.new as any).status === 'completed') {
            queryClient.invalidateQueries({ queryKey });
          }
        }
      )
      .subscribe();

    channels.push(kitchenChannel);

    // Cleanup subscriptions on unmount or when locations change
    return () => {
      channels.forEach((channel) => {
        supabase.removeChannel(channel);
      });
    };
  }, [effectiveLocationIds, queryClient, queryKey]);

  return {
    hourlyData: data?.hourlyData ?? [],
    summary: data?.summary ?? null,
    isLoading,
    error,
    refetch,
    isGlobalMode: globalMode,
    locationCount: effectiveLocationIds.length,
  };
}

function formatHour(hour: number): string {
  if (hour === 0) return '12 AM';
  if (hour === 12) return '12 PM';
  if (hour < 12) return `${hour} AM`;
  return `${hour - 12} PM`;
}

function formatDay(date: Date): string {
  // Format as "Mon 25" for chart display
  const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
  const dayNum = date.getDate();
  return `${dayName} ${dayNum}`;
}